import React from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { Link } from "react-router-dom";
import data from "../data/transparency";
import campaigns from "../data/campaigns";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const Transparency = () => {
  useDocTitle("ZelT | ZelImpact — Transparency");

  const { totals, breakdown, asOf, fiscalYear } = data;
  const available = totals.contributions - totals.used;
  const maxAmount = Math.max(...breakdown.map((b) => b.amount));

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-4xl mx-auto">
          {/* header */}
          <div className="text-center mb-10" data-aos="zoom-in">
            <h1 className="text-4xl font-extrabold text-blue-900 mb-3">
              ZelImpact — Public Transparency
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A nonprofit asking for trust should show its numbers. Here is
              where every dollar came from and where it went — updated by hand
              from our records, as of{" "}
              <b className="text-blue-900">{asOf}</b>.
            </p>
          </div>

          {/* totals cards */}
          <div className="grid gap-5 sm:grid-cols-3 mb-12" data-aos="fade-up">
            <div className="rounded-2xl p-6 text-center shadow-lg bg-gray-50 border-t-4 border-blue-900">
              <p className="text-3xl font-extrabold text-blue-900">
                {fmt(totals.contributions)}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Total Contributions {fiscalYear}
              </p>
            </div>
            <div className="rounded-2xl p-6 text-center shadow-lg bg-gray-50 border-t-4 border-teal-400">
              <p className="text-3xl font-extrabold text-blue-900">
                {fmt(totals.used)}
              </p>
              <p className="text-gray-500 text-sm mt-1">Funds Used</p>
            </div>
            <div className="rounded-2xl p-6 text-center shadow-lg bg-gray-50 border-t-4 border-purple-300">
              <p className="text-3xl font-extrabold text-blue-900">
                {fmt(available)}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Available Program Funds
              </p>
            </div>
          </div>

          {/* breakdown with proportional bars */}
          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 shadow-lg mb-10" data-aos="fade-up">
            <h2 className="text-2xl font-extrabold text-blue-900 mb-6">
              Where Funds Went ({fiscalYear})
            </h2>
            <div className="space-y-5">
              {breakdown.map((b) => {
                const pct = Math.round((b.amount / totals.used) * 100);
                return (
                  <div key={b.category}>
                    <div className="flex justify-between items-baseline mb-1">
                      <p className="font-bold text-blue-900">
                        {b.icon} {b.category}
                      </p>
                      <p className="text-gray-600 font-semibold">
                        {fmt(b.amount)}{" "}
                        <span className="text-gray-400 font-normal text-sm">
                          ({pct}%)
                        </span>
                      </p>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(b.amount / maxAmount) * 100}%`,
                          background: "linear-gradient(90deg, #12bfbe, #9198e5)",
                        }}
                        role="progressbar"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${b.category}: ${fmt(b.amount)}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* current campaigns */}
          {campaigns.filter((c) => c.active).map((c) => {
            const pct = Math.min(100, Math.round((c.raisedCents / c.goalCents) * 100));
            const remaining = Math.max(0, c.goalCents - c.raisedCents);
            const dollars = (cents) => fmt(Math.round(cents / 100));
            return (
              <div
                key={c.id}
                className="mb-10 rounded-2xl overflow-hidden shadow-xl border-2 border-teal-300"
                data-aos="fade-up"
              >
                <div
                  className="p-6 text-white"
                  style={{ background: "linear-gradient(90deg, #1e3a8a, #36395a)" }}
                >
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-teal-400 text-blue-900 mb-3">
                    CURRENT CAMPAIGN
                  </span>
                  <h2 className="text-2xl font-extrabold mb-2">
                    {c.icon} {c.name}
                  </h2>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    {c.tagline}
                  </p>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex-1 h-4 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: "linear-gradient(90deg, #12bfbe, #9198e5)",
                        }}
                        role="progressbar"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                    <p className="font-extrabold text-lg whitespace-nowrap">
                      {pct}%
                    </p>
                  </div>
                  <p className="text-sm text-gray-300">
                    {dollars(c.raisedCents)} raised · {dollars(remaining)} to go ·{" "}
                    goal {dollars(c.goalCents)}
                  </p>
                </div>
                <div className="p-6 bg-white">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {c.story}
                  </p>
                  <h3 className="font-bold text-blue-900 mb-2 text-sm uppercase tracking-wide">
                    What the fund buys (itemized)
                  </h3>
                  <ul className="space-y-1 mb-4">
                    {c.budget.map((b) => (
                      <li
                        key={b.item}
                        className="flex justify-between text-sm text-gray-600"
                      >
                        <span>{b.item}</span>
                        <span className="font-semibold">{dollars(b.amountCents)}</span>
                      </li>
                    ))}
                    <li className="flex justify-between text-sm border-t pt-1 font-bold text-blue-900">
                      <span>Goal</span>
                      <span>{dollars(c.goalCents)}</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-500 italic mb-4">{c.perks}</p>

                  {/* ZelImpact: verified milestones (only when real) */}
                  {c.milestones?.length > 0 && (
                    <div className="mb-4">
                      <h3 className="font-bold text-blue-900 mb-2 text-sm uppercase tracking-wide">
                        🎯 Verified milestones
                      </h3>
                      <ul className="space-y-1.5">
                        {c.milestones.map((m) => (
                          <li
                            key={m.label}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <span
                              className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                m.done
                                  ? "bg-green-500 text-white"
                                  : "bg-gray-200 text-gray-400"
                              }`}
                            >
                              {m.done ? "✓" : "…"}
                            </span>
                            <span className={m.done ? "" : "text-gray-400"}>
                              {m.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ZelImpact: outcome story (only when real) */}
                  {c.outcomeStory && (
                    <div className="bg-teal-50 border-l-4 border-teal-400 rounded-xl p-4 mb-4">
                      <h3 className="font-bold text-blue-900 text-sm uppercase tracking-wide mb-1">
                        💙 What happened after
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {c.outcomeStory}
                      </p>
                    </div>
                  )}
                  <Link
                    to="/contact"
                    className="inline-block px-5 py-2.5 rounded-xl bg-blue-900 text-white font-bold hover:bg-blue-800 transition"
                  >
                    🦈 Fund LAMNA
                  </Link>
                  <p className="text-xs text-gray-400 mt-3">
                    New here? Read about the{" "}
                    <Link
                      to="/credits"
                      className="text-blue-900 font-semibold underline"
                    >
                      LAMNA Credits
                    </Link>{" "}
                    — every donor can claim their name.
                  </p>
                </div>
              </div>
            );
          })}

          {/* honesty notes */}
          <div className="bg-gray-50 rounded-2xl p-6 text-gray-600 text-sm leading-relaxed space-y-3 mb-10" data-aos="fade-up">
            <p>
              📌 These figures reflect management records for fiscal year{" "}
              {fiscalYear} and are unaudited. IRS Form 990 filings are
              available on request (see{" "}
              <Link to="/about" className="text-blue-900 font-semibold underline">
                About
              </Link>
              ).
            </p>
            <p>
              🔒 We never display individual donor information. Members can
              view their own contribution history by logging in.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center" data-aos="fade-up">
            <p className="text-gray-600 mb-4">
              Want to help these numbers grow — and help people at the same
              time?
            </p>
            <Link
              to="/contact"
              className="inline-block px-6 py-3 rounded-xl bg-blue-900 text-white font-bold hover:bg-blue-800 transition"
            >
              💙 Support the Mission
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Transparency;
